/**
 * useRemainingSeats.ts
 * INFINI — Pioneer Seat Counter Hook
 *
 * Subscribes to pioneer_seats via Supabase Realtime.
 * Exposes optimisticClaim() for instant UI feedback on payment initiation.
 * Reconciles with server truth on every DB event — count never drops below 0.
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { supabase } from '@/lib/supabaseClient'
import type { RealtimeChannel } from '@supabase/supabase-js'

// ─── Constants ───────────────────────────────────────────────────────────────

export const PIONEER_TOTAL = 888

export const TIER_CAPACITY: Record<1 | 2 | 3, number> = {
  1: 88,   // Founding Celestial  — seats 001–088
  2: 300,  // Founding Stellar    — seats 089–388
  3: 500,  // Founding Cosmic     — seats 389–888
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type PioneerTier = 1 | 2 | 3

export interface SeatCounts {
  total: number         // always 888
  claimed: number       // confirmed + pending (prevents double-booking)
  remaining: number     // total − claimed, floor 0
  tier1Remaining: number
  tier2Remaining: number
  tier3Remaining: number
  isLoading: boolean
  error: string | null
  /** Call immediately when user initiates payment (before webhook confirms). */
  optimisticClaim: (tier: PioneerTier) => void
  /** Call if payment is abandoned or fails before webhook fires. */
  optimisticRevert: (tier: PioneerTier) => void

  // ── GP1 App.tsx aliases ─────────────────────────────────────────────────
  /** Alias for `remaining` — used by GP1 App.tsx and Screen1/Screen4 */
  remainingSeats: number
  /** Alias for `total` — used by GP1 App.tsx */
  totalSeats: number
}

// Statuses that count as "taken" — pending prevents seat sniping between
// Stripe checkout initiation and webhook confirmation.
const ACTIVE_STATUSES = ['pending', 'confirmed']

// ─── DB Query ─────────────────────────────────────────────────────────────────

interface TierRow {
  tier: number
  count: number
}

async function fetchClaimedByTier(): Promise<Record<PioneerTier, number>> {
  // Count active seats grouped by tier in a single round-trip.
  const { data, error } = await supabase
    .from('pioneer_seats')
    .select('tier')
    .in('status', ACTIVE_STATUSES)

  if (error) throw new Error(error.message)

  const counts: Record<PioneerTier, number> = { 1: 0, 2: 0, 3: 0 }
  for (const row of data ?? []) {
    const t = row.tier as PioneerTier
    if (t in counts) counts[t]++
  }
  return counts
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRemainingSeats(): SeatCounts {
  // Claimed counts sourced from server (ground truth)
  const [serverClaimed, setServerClaimed] = useState<Record<PioneerTier, number>>({
    1: 0, 2: 0, 3: 0,
  })

  // Optimistic delta — added on checkout start, zeroed on server reconcile
  const [optimisticDelta, setOptimisticDelta] = useState<Record<PioneerTier, number>>({
    1: 0, 2: 0, 3: 0,
  })

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const channelRef = useRef<RealtimeChannel | null>(null)

  // ── Fetch + reconcile ──────────────────────────────────────────────────────

  const reconcile = useCallback(async () => {
    try {
      const counts = await fetchClaimedByTier()
      setServerClaimed(counts)
      // Clear optimistic delta for tiers that now have a confirmed server value.
      // Server is the source of truth once it catches up.
      setOptimisticDelta({ 1: 0, 2: 0, 3: 0 })
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch seat data')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // ── Realtime subscription ──────────────────────────────────────────────────

  useEffect(() => {
    reconcile()

    const channel = supabase
      .channel('pioneer_seats_counter')
      .on(
        'postgres_changes',
        {
          event: '*',               // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'pioneer_seats',
        },
        (_payload) => {
          // Re-query rather than diff payload — avoids stale closure bugs
          // and handles multi-tab / webhook races safely.
          reconcile()
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      supabase.removeChannel(channel)
    }
  }, [reconcile])

  // ── Optimistic helpers ─────────────────────────────────────────────────────

  const optimisticClaim = useCallback((tier: PioneerTier) => {
    setOptimisticDelta((prev) => ({
      ...prev,
      [tier]: prev[tier] + 1,
    }))
  }, [])

  const optimisticRevert = useCallback((tier: PioneerTier) => {
    setOptimisticDelta((prev) => ({
      ...prev,
      [tier]: Math.max(0, prev[tier] - 1),
    }))
  }, [])

  // ── Derived values ─────────────────────────────────────────────────────────

  const tierRemaining = (tier: PioneerTier): number => {
    const effective = serverClaimed[tier] + optimisticDelta[tier]
    return Math.max(0, TIER_CAPACITY[tier] - effective)
  }

  const tier1Remaining = tierRemaining(1)
  const tier2Remaining = tierRemaining(2)
  const tier3Remaining = tierRemaining(3)

  const claimed = Math.min(
    PIONEER_TOTAL,
    (serverClaimed[1] + optimisticDelta[1]) +
    (serverClaimed[2] + optimisticDelta[2]) +
    (serverClaimed[3] + optimisticDelta[3])
  )

  const remaining = Math.max(0, PIONEER_TOTAL - claimed)

  return {
    total: PIONEER_TOTAL,
    claimed,
    remaining,
    tier1Remaining,
    tier2Remaining,
    tier3Remaining,
    isLoading,
    error,
    optimisticClaim,
    optimisticRevert,
    // GP1 App.tsx aliases
    remainingSeats: remaining,
    totalSeats: PIONEER_TOTAL,
  }
}
